// A backup is every note as a plain text file in one zip, AES-256 encrypted
// with a password of its own: 7-Zip, Keka or bsdtar open it without the app.
// (Info-ZIP's `unzip` can't: it has no AES.)
import type { Note } from '@/app/db/repo';
import {
  TextReader,
  Uint8ArrayWriter,
  ZipWriter,
} from '@zip.js/zip.js';

export interface Entry {
  path: string;
  text: string;
  modified: Date;
}

const MAX_NAME = 80;

// what a file system would trip on, control characters included
const UNSAFE = /[\u0000-\u001f\u007f/\\:*?"<>|]+/g;

const firstLine = (body: string) =>
  body.split('\n').find(line => line.trim()) ?? '';

const baseName = ({ title, body }: Pick<Note, 'title' | 'body'>) =>
  (title.trim() || firstLine(body))
    .replace(UNSAFE, '-')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, MAX_NAME)
    .trim() || 'untitled';

const folderOf = ({ trashed, archived }: Note) =>
  trashed ? 'trash' : archived ? 'archive' : 'notes';

// "name.txt", then "name (2).txt" and on for notes sharing a name in a
// folder, compared without case as some file systems do
const unique = (paths: string[]) =>
  paths.reduce<{ taken: Set<string>, out: string[] }>(
    ({ taken, out }, path) => {
      const at = (n: number) => n === 1 ? path : `${path} (${n})`;
      const n = [...Array(paths.length).keys()]
        .map(i => i + 1)
        .find(i => !taken.has(at(i).toLowerCase()))!;
      return {
        taken: new Set([...taken, at(n).toLowerCase()]),
        out: [...out, at(n)],
      };
    },
    { taken: new Set(), out: [] },
  ).out;

export const entriesOf = (notes: Note[]): Entry[] => {
  const paths = unique(notes.map(n => `${folderOf(n)}/${baseName(n)}`));
  return notes.map((note, i) => ({
    path: `${paths[i]}.txt`,
    text: note.title.trim() ? `${note.title}\n\n${note.body}` : note.body,
    modified: new Date(note.updatedAt),
  }));
};

export const makeArchive = async (notes: Note[], password: string) => {
  const zip = new ZipWriter(new Uint8ArrayWriter(), {
    password,
    encryptionStrength: 3,
    useWebWorkers: false,
  });
  for (const { path, text, modified } of entriesOf(notes)) {
    await zip.add(path, new TextReader(text), { lastModDate: modified });
  }
  return zip.close();
};
